import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AIPlannerComponent.css';
import Loader from '../../Loader.jsx'; // Import the Loader component
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faToggleOn, faToggleOff, faExpand, faEdit } from '@fortawesome/free-solid-svg-icons';

const AIPlannerComponent = () => {
  const [inputText, setInputText] = useState('');
  const [generatedPlan, setGeneratedPlan] = useState('');
  const [schedules, setSchedules] = useState(() => {
    const savedSchedules = localStorage.getItem('schedules');
    return savedSchedules ? JSON.parse(savedSchedules) : [];
  });
  const [activeSchedule, setActiveSchedule] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTaskIndex, setEditedTaskIndex] = useState(null);
  const [editedTask, setEditedTask] = useState('');
  const [editedTime, setEditedTime] = useState('');
  const [loading, setLoading] = useState(false); // State for loading animation
  const notificationTimeouts = useRef({});

  useEffect(() => {
    if (Notification.permission !== 'granted') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('schedules', JSON.stringify(schedules));
  }, [schedules]);

  const cancelAllNotifications = useCallback(() => {
    Object.values(notificationTimeouts.current).forEach(timeoutId => {
      clearTimeout(timeoutId);
    });
    notificationTimeouts.current = {};
  }, []);

  const sendNotification = useCallback((task, time) => {
    new Notification(`Task Reminder: ${task}`, {
      body: `It's ${time}. Time to ${task}`,
    });

    const utterance = new SpeechSynthesisUtterance(`It's ${time}. Time to ${task}`);
    window.speechSynthesis.speak(utterance);
  }, []);

  const scheduleNotifications = useCallback((schedule) => {
    cancelAllNotifications(); // Cancel any existing notifications

    schedule.tasks.forEach(task => {
      const taskTime = new Date(task.timestamp);
      const now = new Date();

      if (taskTime > now) {
        const timeout = taskTime.getTime() - now.getTime();
        const timeoutId = setTimeout(() => {
          if (activeSchedule === schedule) {
            sendNotification(task.task, task.time);
          }
        }, timeout);
        notificationTimeouts.current[task.id] = timeoutId;
      }
    });
  }, [sendNotification, activeSchedule, cancelAllNotifications]);

  useEffect(() => {
    if (activeSchedule) {
      scheduleNotifications(activeSchedule);
    } else {
      cancelAllNotifications();
    }
  }, [activeSchedule, scheduleNotifications, cancelAllNotifications]);

  const handleInputChange = (e) => {
    setInputText(e.target.value);
  };

  const handleGeneratePlan = async () => {
    setLoading(true); // Start loading animation
    const url = 'https://chatgpt-42.p.rapidapi.com/conversationgpt4-2';
    const options = {
      method: 'POST',
      headers: {
        'x-rapidapi-key': process.env.REACT_APP_RAPIDAPI_KEY,
        'x-rapidapi-host': 'chatgpt-42.p.rapidapi.com',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        messages: [
          {
            role: 'user',
            content: inputText
          }
        ],
        system_prompt: '',
        temperature: 0.9,
        top_k: 5,
        top_p: 0.9,
        max_tokens: 256,
        web_access: false
      })
    };

    try {
      const response = await fetch(url, options);
      const data = await response.json();
      const simulatedResponse = data.choices[0].message.content.trim();
      setGeneratedPlan(simulatedResponse);

      if (simulatedResponse) {
        const newSchedule = parseGeneratedPlan(simulatedResponse);
        setSchedules([...schedules, newSchedule]);
        setActiveSchedule(newSchedule);
      }
    } catch (error) {
      console.error('Error generating plan:', error);
      generateDefaultPlan();
    } finally {
      setLoading(false); // Stop loading animation
    }
  };

  const generateDefaultPlan = () => {
    let fallbackResponse = '';

    if (inputText.toLowerCase().includes('daily plan')) {
      fallbackResponse = `6:00 AM - Wake up\n7:00 AM - Exercise\n8:00 AM - Breakfast\n9:00 AM - Start work\n12:00 PM - Lunch\n1:00 PM - Continue work\n6:00 PM - Wrap up work\n7:00 PM - Dinner\n8:00 PM - Relax\n10:00 PM - Bedtime`;
    } else if (inputText.toLowerCase().includes('work schedule')) {
      fallbackResponse = `Day 1: Research\nDay 2: Outline\nDay 3: Draft\nDay 4: Review\nDay 5: Finalize\nDay 6: Presentation prep\nDay 7: Present`;
    } else {
      fallbackResponse = `I'm sorry, I couldn't generate a specific plan based on your input. Please provide more details.`;
    }

    setGeneratedPlan(fallbackResponse);
    const newSchedule = parseGeneratedPlan(fallbackResponse);
    setSchedules([...schedules, newSchedule]);
    setActiveSchedule(newSchedule);
  };

  const parseGeneratedPlan = (plan) => {
    const tasks = plan.split('\n').map((line, index) => {
      const [time, task] = line.split(' - ');
      const taskTime = parseTimeString(time.trim());

      return {
        time: time.trim(),
        task: task ? task.trim() : `Task ${index + 1}`,
        id: index,
        timestamp: taskTime.getTime(),
      };
    });

    return {
      id: Date.now(),
      name: `Schedule ${schedules.length + 1}`,
      tasks,
    };
  };

  const parseTimeString = (timeString) => {
    const [time, period] = timeString.split(' ');
    let [hours, minutes] = time.split(':').map(Number);

    if (period.toUpperCase() === 'PM' && hours !== 12) {
      hours += 12;
    }
    if (period.toUpperCase() === 'AM' && hours === 12) {
      hours = 0;
    }

    const now = new Date();
    now.setHours(hours, minutes, 0, 0);
    return now;
  };

  const toggleSchedule = (schedule) => {
    if (activeSchedule === schedule) {
      setActiveSchedule(null);
      cancelAllNotifications();
    } else {
      setActiveSchedule(schedule);
      scheduleNotifications(schedule);
    }
  };

  const deleteSchedule = (id) => {
    if (activeSchedule && activeSchedule.id === id) {
      cancelAllNotifications(); // Cancel notifications if the active schedule is being deleted
      setActiveSchedule(null);
    }
    const updatedSchedules = schedules.filter(schedule => schedule.id !== id);
    setSchedules(updatedSchedules);
    localStorage.setItem('schedules', JSON.stringify(updatedSchedules));
  };

  const handleExpand = (schedule) => {
    setSelectedSchedule(schedule);
  };

  const handleClosePopup = () => {
    setSelectedSchedule(null);
    setIsEditing(false);
    setEditedTaskIndex(null);
    setEditedTask('');
    setEditedTime('');
  };

  const startEditingTask = (index, task) => {
    setIsEditing(true);
    setEditedTaskIndex(index);
    setEditedTask(task.task);
    setEditedTime(task.time);
  };

  const saveEditedTask = () => {
    if (selectedSchedule && editedTaskIndex !== null) {
      const updatedTasks = selectedSchedule.tasks.map((task, index) =>
        index === editedTaskIndex ? { ...task, task: editedTask, time: editedTime } : task
      );
      const updatedSchedule = { ...selectedSchedule, tasks: updatedTasks };
      const updatedSchedules = schedules.map(schedule =>
        schedule.id === selectedSchedule.id ? updatedSchedule : schedule
      );
      setSchedules(updatedSchedules);
      setSelectedSchedule(updatedSchedule);
      localStorage.setItem('schedules', JSON.stringify(updatedSchedules));

      setIsEditing(false);
      setEditedTaskIndex(null);
      setEditedTask('');
      setEditedTime('');
    }
  };

  return (
    <div className="ai-planner-container">
      <h2>AI Planner</h2>
      <textarea
        value={inputText}
        onChange={handleInputChange}
        placeholder="Describe your task or schedule requirements..."
        className="ai-input"
      />
      <button onClick={() => setInputText('Daily Plan')} className="default-command-button">Daily Plan</button>
      <button onClick={() => setInputText('Work Schedule')} className="default-command-button">Work Schedule</button>
      <button onClick={handleGeneratePlan} className="ai-generate-button">Generate Plan</button>
      {loading && (
        <div className="loading-overlay">
          <Loader /> {/* Loader in the middle of the page */}
        </div>
      )}
      {generatedPlan && (
        <div className="ai-generated-plan">
          <h3>Generated Plan</h3>
          <pre>{generatedPlan}</pre>
        </div>
      )}
      <div className="bottom-right-cards">
        {schedules.map((schedule, index) => (
          <div key={index} className={`planner-card ${activeSchedule === schedule ? 'active' : ''}`}>
            <div className="planner-card-header">
              <h4>{schedule.name}</h4>
              <div className="planner-card-actions">
                <button className="toggle-button" onClick={() => toggleSchedule(schedule)}>
                  <FontAwesomeIcon icon={activeSchedule === schedule ? faToggleOn : faToggleOff} />
                </button>
                <button className="expand-button" onClick={() => handleExpand(schedule)}>
                  <FontAwesomeIcon icon={faExpand} />
                </button>
                <button className="delete-button" onClick={() => deleteSchedule(schedule.id)}> 
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
            <div className="planner-card-body">
              <p>{schedule.tasks.slice(0, 3).map(task => `${task.time} - ${task.task}`).join(', ')}</p>
            </div>
          </div>
        ))}
      </div>

      {selectedSchedule && (
        <div className="popup-overlay" onClick={handleClosePopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <h3>{selectedSchedule.name}</h3>
            <ul>
              {selectedSchedule.tasks.map((task, index) => (
                <li key={index}>
                  {isEditing && editedTaskIndex === index ? (
                    <>
                      <input
                        type="text"
                        value={editedTime}
                        onChange={(e) => setEditedTime(e.target.value)}
                        placeholder="Time"
                      />
                      <input
                        type="text"
                        value={editedTask}
                        onChange={(e) => setEditedTask(e.target.value)}
                        placeholder="Task"
                      />
                      <button className='save_button' onClick={saveEditedTask}>Save</button>
                    </>
                  ) : (
                    <>
                      {task.time} - {task.task}
                      <button onClick={() => startEditingTask(index, task)}>
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
            <button onClick={handleClosePopup} className="close-popup-button">Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIPlannerComponent;
